import { MouseEventHandler, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import { useHistory } from 'react-router-dom';

import { useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import BaseAppBar from '@o2/common-business/AppBar';
import SidebarMenu from '@o2/common-business/SidebarMenu';
import { Basket, CustomerAvatarResident, LogoMojeO2 } from '@o2/icons';
import { Link } from '@o2/ui';
import Box from '@o2/ui/Box';

import UserPopper from './UserPopper';
import { CustomerDetail, CustomerExtendedTypeEnum, CustomerSegment } from 'generated/bffCustomer/models';
import { SharedObjectsData } from 'generated/sharedObjects/models';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';
import { AppState } from 'helpers/store/models/AppState';
import { useFilterUserPrivilegeResourceList } from 'hooks/useFilterUserPrivilegeResourceList';
import { useHasSimpleUserPrivileges } from 'hooks/useHasSimpleUserPrivileges';
import { useTranslationWithDataKey } from 'hooks/useTranslationWithDataKey';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { ExternalUser } from 'models/context/ExternalUser';
import { InternalUser } from 'models/context/InternalUser';
import { OrderInfo } from 'models/context/OrderInfo';
import { LegacyCrmDeeplinkCommand, LegacyCrmDeeplinkConfig } from 'models/LegacyCrmDeeplink';
import { AuthUserType, loginActions, selectAuthUserType } from 'saga/auth/ducks';
import { selectContextCustomerInfo, selectCustomerList } from 'saga/context/customer/ducks';
import { selectContextExternalUser } from 'saga/context/external/ducks';
import { selectContextInternalUser } from 'saga/context/internal/ducks';
import { selectContextOrderInfo } from 'saga/context/order/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import { getCustomerDetailActions, selectCustomerDetail } from 'saga/customerDetail/ducks';
import { selectProperty } from 'saga/property/ducks';
import { selectSharedObjectsData } from 'saga/sharedObjects/ducks';
import HasSimpleUserPrivilege from 'views/components/ACL/HasSimpleUserPrivilege';
import { isUnidentifiedResidentCustomer } from 'views/layout/NewLayout/utils';
import { environment } from 'environments/environment';

const IconLink = styled(Link)(() => ({
  display: 'flex',
  alignItems: 'center',
  borderBottom: 'unset',
  '&:hover': {
    borderBottom: 'unset',
  },
}));

const omnichannelUrlProperty = 'gui.externalHeader.action.omnichannelUrl';

const apfDeeplinkUrlProperty = 'apf.deeplinker.url';
const paymentsOverviewInternalDeeplinkProperty = 'mo2Care.PaymentsOverview.internal.deeplink';
const paymentsOverviewExternalDeeplinkProperty = 'mo2Care.PaymentsOverview.external.deeplink';
const cpDashboardDeeplinkProperty = 'mo2Care.CpDashboard.deeplink';

const NewAppBar = ({
  authUserType,
  contextExternalUser,
  contextCustomer,
  customerList,
  redirectToLegacyCrm,
  orderInfo,
  contextInternalUser,
  login,
  sharedObjectData,
  getCustomerDetail,
  customerDetail,
  omnichannelURL,
  apfDeeplinkUrl,
  paymentsOverviewInternalDeeplink,
  paymentsOverviewExternalDeeplink,
  cpDashboardDeeplink,
}: StateToProps & DispatchToProps) => {
  const { t } = useTranslationWithDataKey();
  const history = useHistory();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [incompleteCustomer, setIncompleteCustomer] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [popoverElm, setPopoverElm] = useState({
    id: null, // what popover to open
    target: null, // where to open it (anchor)
  });

  const missingName = environment.properties['gui.general.BFFDIGITVCAPTURE.contactPerson.missingName'];

  const privilegeType = 'INCOMP_CUST_DATA_VERIFI';
  const resourceType = 'SALES_CHANNEL';
  const resourcesList = ['SHOP', 'CALL_CENTER'];

  const ACLsForIncompleteUser = useFilterUserPrivilegeResourceList('', privilegeType, resourceType, resourcesList);

  const {
    BILL_VIEW: billViewAcl,
    DOCUMENT_VIEW: documentViewAcl,
    ORDER_CREATE: orderCreateAcl,
  } = useHasSimpleUserPrivileges(['BILL_VIEW', 'DOCUMENT_VIEW', 'ORDER_CREATE']);

  const showInvoicesLink = contextCustomer && billViewAcl && !isUnidentifiedResidentCustomer(contextCustomer);

  const showDocumentsLink = contextCustomer && documentViewAcl && !isUnidentifiedResidentCustomer(contextCustomer);

  const show02Link = !incompleteCustomer && !isSmallScreen;

  const showMyServicesLink = (contextCustomer || contextExternalUser) && !incompleteCustomer;

  const showCustomerLoginLink = !contextExternalUser && !contextInternalUser;

  const showModelationsLink =
    contextInternalUser &&
    !incompleteCustomer &&
    (contextCustomer?.segment === CustomerSegment.SMB || contextCustomer?.segment === CustomerSegment.R);

  const showPaymentsLink = isUnidentifiedResidentCustomer(contextCustomer);

  const showCpDashboardLink = !contextInternalUser && customerList.length > 1;

  const showBasket = !incompleteCustomer && orderCreateAcl && !isUnidentifiedResidentCustomer(contextCustomer);

  const getCustomerName = () => {
    if (isUnidentifiedResidentCustomer(contextCustomer) && contextExternalUser?.firstName === missingName) {
      return `${t('onecrm.gui_header.externalHeader.customer', false)} ${contextExternalUser.login}`;
    }

    if (isUnidentifiedResidentCustomer(contextCustomer) && !contextExternalUser && contextInternalUser) {
      return `${t('onecrm.gui_header.externalHeader.unidentifiedCustomer', false)}`;
    }

    if (contextCustomer && !(contextExternalUser && contextExternalUser?.firstName === missingName)) {
      return `${t('onecrm.gui_header.externalHeader.customer', false)} ${contextCustomer.displayName}`;
    }
  };

  const tooltipTitle = useMemo(() => {
    if (contextExternalUser) {
      return (
        <Box>
          {contextCustomer && (
            <>
              {getCustomerName()}
              <br />
            </>
          )}
          {t('onecrm.gui_header.externalHeader.access.via')}: {contextExternalUser.login}
        </Box>
      );
    } else if (contextCustomer) {
      return <Box>{getCustomerName()}</Box>;
    }

    return '';
  }, [contextCustomer, contextExternalUser]);

  useEffect(() => {
    if (!initialized) {
      if (contextCustomer) {
        getCustomerDetail(contextCustomer.cuRefNo);
      }

      setInitialized(true);
    }
  }, [initialized, contextCustomer, getCustomerDetail]);

  useEffect(() => {
    if (
      customerDetail &&
      customerDetail?.extendedType === CustomerExtendedTypeEnum.INCOMPLETE_DATA &&
      contextInternalUser &&
      ACLsForIncompleteUser.length
    ) {
      setIncompleteCustomer(true);
    }
  }, [customerDetail, contextInternalUser, ACLsForIncompleteUser]);

  const handleMyServicesClick: MouseEventHandler = () => {
    if (authUserType === 'internal') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { cuRefNo: contextCustomer?.cuRefNo },
      });
    } else if (authUserType === 'external') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.EMO2DASH,
        queryParams: { cuRefNo: contextCustomer?.cuRefNo },
      });
    }
  };

  const handleInvoicesClick: MouseEventHandler = () => {
    history.push(buildRoute(ROUTES.Billing));
  };

  const handleDocumentsClick: MouseEventHandler = () => {
    history.push(buildRoute(ROUTES.DocumentList));
  };

  const handleO2ButtonClick = () => {
    window.location.href = t('onecrm.gui_externalHeader.action.o2czURL', false);
  };

  const handleModelationsClick = () => {
    sharedObjectData?.interaction?.interactionId
      ? window.open(omnichannelURL, '_blank')
      : (window.location.href = omnichannelURL);
  };

  const handlePaymentsClick: MouseEventHandler = () => {
    const deeplink = contextInternalUser ? paymentsOverviewInternalDeeplink : paymentsOverviewExternalDeeplink;
    const url = `${apfDeeplinkUrl}${deeplink}`;

    window.location.href = url;
  };

  const handleCpDashboardClick: MouseEventHandler = () => {
    const url = `${apfDeeplinkUrl}${cpDashboardDeeplink}${contextExternalUser?.cpRefNo}`;

    window.location.href = url;
  };

  const handlePopoverClose = () => {
    setPopoverElm({
      id: null,
      target: null,
    });
  };
  const handlePopoverOpen = (i: any) => (evt: any) => {
    if (popoverElm?.id === i) {
      return handlePopoverClose();
    }

    setPopoverElm({
      id: i,
      target: evt.currentTarget,
    });
  };

  const manageHandlePopoverOpen = () => {
    if (contextCustomer || contextExternalUser) {
      return handlePopoverOpen('userPopover2');
    } else if (!contextExternalUser && !contextInternalUser) {
      console.log('TODO OWN');
    }
  };

  const redirectToCustomerDashboard = () => {
    if (!contextCustomer) {
      return;
    }

    if (authUserType === 'internal') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { cuRefNo: contextCustomer.cuRefNo },
      });
    } else if (authUserType === 'external') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.EMO2DASH,
        queryParams: { cuRefNo: contextCustomer.cuRefNo },
      });
    }
  };

  return (
    <BaseAppBar>
      <BaseAppBar.Container>
        <BaseAppBar.LogoButton data-elm="11.1.3_002">
          {contextCustomer ? (
            <IconLink onClick={() => redirectToCustomerDashboard()}>
              <LogoMojeO2 />
            </IconLink>
          ) : (
            <LogoMojeO2 />
          )}
        </BaseAppBar.LogoButton>
        <BaseAppBar.Menu moreButtonText={t('o2-ui.AppBar.moreButton')}>
          {showCpDashboardLink && (
            <BaseAppBar.MenuItem data-elm="11.1.3_031" onClick={handleCpDashboardClick}>
              {t('onecrm.gui_externalHeader.action.cpDashboard')}
            </BaseAppBar.MenuItem>
          )}
          {showMyServicesLink && (
            <BaseAppBar.MenuItem data-elm="11.1.3_005" onClick={handleMyServicesClick}>
              {t('onecrm.gui_externalHeader.action.myServices')}
            </BaseAppBar.MenuItem>
          )}
          {showInvoicesLink && (
            <BaseAppBar.MenuItem data-elm="11.1.3_006" onClick={handleInvoicesClick}>
              {t('onecrm.gui_externalHeader.action.invoices')}
            </BaseAppBar.MenuItem>
          )}
          {showPaymentsLink && (
            <BaseAppBar.MenuItem data-elm="11.1.3_025" onClick={handlePaymentsClick}>
              {t('onecrm.gui_externalHeader.action.payments')}
            </BaseAppBar.MenuItem>
          )}
          {showDocumentsLink && (
            <BaseAppBar.MenuItem data-elm="11.1.3_007" onClick={handleDocumentsClick}>
              {t('onecrm.gui_externalHeader.action.documnets')}
            </BaseAppBar.MenuItem>
          )}
          {showModelationsLink && (
            <HasSimpleUserPrivilege privilegeType="REDIRECT_OMNI">
              <BaseAppBar.MenuItem data-elm="11.1.3_030" onClick={handleModelationsClick} internal>
                {t('onecrm.gui_externalHeader.modelations')}
              </BaseAppBar.MenuItem>
            </HasSimpleUserPrivilege>
          )}
        </BaseAppBar.Menu>
      </BaseAppBar.Container>
      <BaseAppBar.Controls>
        {showBasket && (
          <BaseAppBar.Button
            data-elm="11.1.3_009"
            icon={<Basket />}
            showBadge={!!(orderInfo && orderInfo.itemsCount > 0)}
            onClick={() =>
              redirectToLegacyCrm({
                command: LegacyCrmDeeplinkCommand.MO2BASKET,
                queryParams: {
                  ...(!(orderInfo && orderInfo.itemsCount > 0) && { scenario: 'spaceExtend' }),
                  step: 'basket',
                },
              })
            }
          />
        )}
        {show02Link && (
          <BaseAppBar.Button data-elm="11.1.3_010" onClick={handleO2ButtonClick}>
            {t('onecrm.gui_externalHeader.action.o2cz')}
          </BaseAppBar.Button>
        )}

        {!showCustomerLoginLink && (
          <BaseAppBar.UserButton
            data-elm="11.1.3_003"
            onClick={manageHandlePopoverOpen()}
            customerType={contextCustomer?.segment === 'R' || !contextCustomer ? 'resident' : 'business'}
            showChevron
            aria-describedby="userPopover2"
            aria-haspopup="true"
            title={(contextExternalUser || contextInternalUser) && getCustomerName()}
            subtitle={
              <span data-elm="11.1.3_026">
                {contextExternalUser
                  ? `${t('onecrm.gui_header.externalHeader.O2ID', false)} ${contextExternalUser.login}`
                  : ''}
              </span>
            }
            tooltip={tooltipTitle}
            tooltipProps={{
              'data-elm': '11.1.3_029',
            }}
          />
        )}
        {showCustomerLoginLink && (
          <BaseAppBar.LoginButton onClick={login} data-elm="11.1.3_003" icon={<CustomerAvatarResident height={28} />}>
            {t('onecrm.gui_externalHeader.action.login')}
          </BaseAppBar.LoginButton>
        )}
        {isSmallScreen && (
          <BaseAppBar.MobileMenu buttonElmId="11.1.3_004" logo={<LogoMojeO2 />} drawerZIndex={1270}>
            <SidebarMenu.List>
              {(contextCustomer || contextExternalUser) && (
                <SidebarMenu.Button data-elm="11.1.3_005" onClick={handleMyServicesClick}>
                  {t('onecrm.gui_externalHeader.action.myServices')}
                </SidebarMenu.Button>
              )}
              {showInvoicesLink && (
                <SidebarMenu.Button data-elm="11.1.3_006" onClick={handleInvoicesClick}>
                  {t('onecrm.gui_externalHeader.action.invoices')}
                </SidebarMenu.Button>
              )}
              {showPaymentsLink && (
                <SidebarMenu.Button data-elm="11.1.3_025" onClick={handlePaymentsClick}>
                  {t('onecrm.gui_externalHeader.action.payments')}
                </SidebarMenu.Button>
              )}
              {showDocumentsLink && (
                <SidebarMenu.Button data-elm="11.1.3_007" onClick={handleDocumentsClick}>
                  {t('onecrm.gui_externalHeader.action.documnets')}
                </SidebarMenu.Button>
              )}
              {showModelationsLink && (
                <HasSimpleUserPrivilege privilegeType="REDIRECT_OMNI">
                  <SidebarMenu.Button data-elm="11.1.3_030" onClick={handleModelationsClick} internal>
                    {t('onecrm.gui_externalHeader.modelations')}
                  </SidebarMenu.Button>
                </HasSimpleUserPrivilege>
              )}
            </SidebarMenu.List>
          </BaseAppBar.MobileMenu>
        )}
      </BaseAppBar.Controls>
      <UserPopper
        id="userPopover2"
        popoverElm={popoverElm}
        handlePopoverClose={handlePopoverClose}
        incompleteCustomer={incompleteCustomer}
      />
    </BaseAppBar>
  );
};

type StateToProps = {
  contextCustomer: CustomerInfo | null;
  customerDetail: CustomerDetail | null;
  customerList: CustomerInfo[];
  contextExternalUser: ExternalUser | null;
  authUserType: AuthUserType;
  orderInfo: OrderInfo | null;
  contextInternalUser: InternalUser | null;
  sharedObjectData: SharedObjectsData | null;
  omnichannelURL: string;
  apfDeeplinkUrl: string;
  paymentsOverviewInternalDeeplink: string;
  paymentsOverviewExternalDeeplink: string;
  cpDashboardDeeplink: string;
};

const mapStateToProps = (state: AppState): StateToProps => ({
  contextCustomer: selectContextCustomerInfo(state),
  customerDetail: selectCustomerDetail(state),
  customerList: selectCustomerList(state),
  contextExternalUser: selectContextExternalUser(state),
  authUserType: selectAuthUserType(state),
  orderInfo: selectContextOrderInfo(state),
  contextInternalUser: selectContextInternalUser(state),
  sharedObjectData: selectSharedObjectsData(state),
  omnichannelURL: selectProperty(omnichannelUrlProperty),
  apfDeeplinkUrl: selectProperty(apfDeeplinkUrlProperty),
  paymentsOverviewInternalDeeplink: selectProperty(paymentsOverviewInternalDeeplinkProperty),
  paymentsOverviewExternalDeeplink: selectProperty(paymentsOverviewExternalDeeplinkProperty),
  cpDashboardDeeplink: selectProperty(cpDashboardDeeplinkProperty),
});

const mapDispatchToProps = {
  redirectToLegacyCrm: redirectToLegacyCrmAction,
  login: loginActions.request,
  getCustomerDetail: getCustomerDetailActions.request,
};

type DispatchToProps = {
  login: () => void;
  redirectToLegacyCrm: (deeplinkConfig: LegacyCrmDeeplinkConfig) => void;
  getCustomerDetail: (payload: any) => void;
};

export default connect(mapStateToProps, mapDispatchToProps)(NewAppBar);
